import { useState, useRef, useEffect, useCallback } from "react";
import axios from "axios";

const API = "/api";

const VoiceChat = () => {
  const [isListening, setIsListening] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [messages, setMessages] = useState([]);
  const [error, setError] = useState("");
  const [volume, setVolume] = useState(0);
  const [connectionStatus, setConnectionStatus] = useState("idle");
  const [textInput, setTextInput] = useState("");
  const [showTextInput, setShowTextInput] = useState(false);
  
  const recognitionRef = useRef(null);
  const synthRef = useRef(window.speechSynthesis);
  const messagesEndRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const animationRef = useRef(null);
  const silenceTimeoutRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Volume analyzer for visual feedback
  const startVolumeAnalysis = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      analyserRef.current = audioContextRef.current.createAnalyser();
      const source = audioContextRef.current.createMediaStreamSource(stream);
      source.connect(analyserRef.current);
      analyserRef.current.fftSize = 256;
      
      const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
      
      const updateVolume = () => {
        if (analyserRef.current) {
          analyserRef.current.getByteFrequencyData(dataArray);
          const average = dataArray.reduce((a, b) => a + b) / dataArray.length;
          setVolume(average / 255);
          animationRef.current = requestAnimationFrame(updateVolume);
        }
      };
      
      updateVolume();
    } catch (e) {
      console.error("Could not start volume analysis:", e);
    }
  }, []);

  const stopVolumeAnalysis = useCallback(() => {
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
    setVolume(0);
  }, []);

  // Initialize Speech Recognition
  useEffect(() => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      
      // Detect mobile device
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      
      // On mobile, use single-shot mode to avoid the beeping loop
      recognitionRef.current.continuous = !isMobile;
      recognitionRef.current.interimResults = true;
      recognitionRef.current.lang = 'pt-PT';
      recognitionRef.current.maxAlternatives = 1;
      
      console.log(`Speech recognition initialized (mobile: ${isMobile}, continuous: ${!isMobile})`);
      
      // Track if we're currently processing to avoid duplicate sends
      let pendingTranscript = '';
      let sendTimeout = null;
      
      recognitionRef.current.onresult = (event) => {
        let interimTranscript = '';
        let finalTranscript = '';
        
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalTranscript += transcript;
          } else {
            interimTranscript += transcript;
          }
        }
        
        const currentTranscript = finalTranscript || interimTranscript;
        setTranscript(currentTranscript);
        pendingTranscript = currentTranscript;
        
        console.log(`Transcript: "${currentTranscript}" (final: ${!!finalTranscript})`);
        
        // Reset silence timeout when speech detected
        if (silenceTimeoutRef.current) {
          clearTimeout(silenceTimeoutRef.current);
        }
        
        // Clear any pending send
        if (sendTimeout) {
          clearTimeout(sendTimeout);
        }
        
        // On mobile or when we have a final transcript, send after a short delay
        if (finalTranscript && finalTranscript.trim().length > 2) {
          console.log("Final transcript detected:", finalTranscript);
          handleUserMessage(finalTranscript);
          pendingTranscript = '';
        } else if (isMobile && interimTranscript && interimTranscript.trim().length > 2) {
          // On mobile, also trigger on interim after silence (user stopped speaking)
          sendTimeout = setTimeout(() => {
            if (pendingTranscript && pendingTranscript.trim().length > 2 && !isProcessing) {
              console.log("Mobile: sending after silence:", pendingTranscript);
              handleUserMessage(pendingTranscript);
              pendingTranscript = '';
            }
          }, 1500); // Wait 1.5s of silence before sending on mobile
        }
      };
      
      recognitionRef.current.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        if (event.error === 'no-speech') {
          // On mobile, this is normal - just restart
          setConnectionStatus("listening");
          if (isMobile && isListening && !isProcessing && !isSpeaking) {
            setTimeout(() => {
              try {
                recognitionRef.current?.start();
              } catch (e) {
                // Ignore
              }
            }, 500);
          }
        } else if (event.error === 'network') {
          setError("Erro de rede. Verifica a tua conexão.");
          setConnectionStatus("error");
        } else if (event.error === 'aborted') {
          // User or system aborted - ignore
        } else if (event.error === 'not-allowed') {
          setError("Permissão de microfone negada. Permite o acesso nas configurações.");
          setConnectionStatus("error");
        } else {
          setError(`Erro: ${event.error}`);
        }
      };
      
      recognitionRef.current.onend = () => {
        console.log("Recognition ended, isListening:", isListening, "isProcessing:", isProcessing);
        
        // Send pending transcript if any (mobile fallback)
        if (pendingTranscript && pendingTranscript.trim().length > 2 && !isProcessing) {
          console.log("Sending pending transcript on end:", pendingTranscript);
          handleUserMessage(pendingTranscript);
          pendingTranscript = '';
          return; // Don't restart yet, wait for response
        }
        
        // Auto-restart listening if still in listening mode
        if (isListening && !isProcessing && !isSpeaking) {
          // Longer delay on mobile to avoid rapid restarts (beeping)
          const restartDelay = isMobile ? 800 : 100;
          setTimeout(() => {
            try {
              if (recognitionRef.current && isListening && !isProcessing && !isSpeaking) {
                recognitionRef.current.start();
                setConnectionStatus("listening");
                console.log("Recognition restarted");
              }
            } catch (e) {
              console.log("Restart skipped:", e.message);
            }
          }, restartDelay);
        }
      };

      recognitionRef.current.onspeechstart = () => {
        setConnectionStatus("hearing");
        console.log("Speech started");
      };

      recognitionRef.current.onspeechend = () => {
        setConnectionStatus("listening");
        console.log("Speech ended");
      };

    } else {
      setError("O teu browser não suporta reconhecimento de voz. Usa Chrome ou Edge.");
    }
    
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.abort();
      }
      synthRef.current.cancel();
      stopVolumeAnalysis();
    };
  }, [isListening, isProcessing, isSpeaking, stopVolumeAnalysis]);

  const startListening = useCallback(() => {
    if (recognitionRef.current && !isProcessing && !isSpeaking) {
      try {
        // Request microphone permission first
        navigator.mediaDevices.getUserMedia({ audio: true })
          .then(() => {
            recognitionRef.current.start();
            setConnectionStatus("listening");
            setError("");
            startVolumeAnalysis();
            console.log("Speech recognition started successfully");
          })
          .catch((err) => {
            console.error("Microphone permission denied:", err);
            setError("Permissão de microfone negada. Por favor, permite o acesso ao microfone nas configurações do browser.");
            setConnectionStatus("error");
            setIsListening(false);
          });
      } catch (e) {
        console.error("Error starting recognition:", e);
        // Try to restart
        setTimeout(() => {
          try {
            recognitionRef.current?.start();
          } catch (e2) {
            console.error("Retry failed:", e2);
          }
        }, 500);
      }
    }
  }, [isProcessing, isSpeaking, startVolumeAnalysis]);

  const handleUserMessage = async (text) => {
    if (!text.trim() || isProcessing) return;
    
    setIsProcessing(true);
    setTranscript("");
    setConnectionStatus("processing");
    setError("");
    
    // Stop listening while processing
    if (recognitionRef.current) {
      try {
        recognitionRef.current.abort();
      } catch (e) {
        // Ignore
      }
    }
    
    // Add user message
    const newUserMsg = { role: "user", content: text, timestamp: new Date() };
    setMessages(prev => [...prev, newUserMsg]);
    
    try {
      console.log("Sending message to API:", text);
      
      // Send to backend
      const response = await axios.post(`${API}/chat/voice`, {
        message: text,
        chat_id: "voice_continuous_user"
      }, { timeout: 90000 });
      
      console.log("API response:", response.data);
      
      const assistantMessage = response.data.response || "Desculpa, não consegui processar.";
      const audioBase64 = response.data.audio;
      
      // Add assistant message
      const newAssistantMsg = { role: "assistant", content: assistantMessage, timestamp: new Date() };
      setMessages(prev => [...prev, newAssistantMsg]);
      
      // Play audio from backend if available, otherwise use browser TTS
      if (audioBase64) {
        console.log("Playing audio from backend TTS");
        playBackendAudio(audioBase64);
      } else {
        console.log("No backend audio, using browser TTS");
        speakResponse(assistantMessage);
      }
      
    } catch (error) {
      console.error("Chat error:", error);
      console.error("Error details:", error.response?.data || error.message);
      setConnectionStatus("error");
      
      let errorMsg = "Houve um erro na comunicação.";
      if (error.code === 'ECONNABORTED') {
        errorMsg = "A resposta demorou demasiado. Tenta novamente.";
      } else if (error.response?.status === 500) {
        errorMsg = "Erro no servidor. Tenta novamente em alguns segundos.";
      } else if (!navigator.onLine) {
        errorMsg = "Sem conexão à internet.";
      }
      
      setError(errorMsg);
      setMessages(prev => [...prev, { role: "assistant", content: errorMsg, timestamp: new Date() }]);
      
      setIsProcessing(false);
      setIsSpeaking(false);
      
      // Restart listening after error
      if (isListening) {
        setTimeout(() => startListening(), 2000);
      }
    }
  };

  const speakResponse = (text) => {
    setIsSpeaking(true);
    setConnectionStatus("speaking");
    
    // Cancel any ongoing speech
    synthRef.current.cancel();
    
    // Clean text for speech
    const cleanText = text
      .replace(/\*/g, '')
      .replace(/[_~`]/g, '')
      .replace(/https?:\/\/\S+/g, 'link')
      .substring(0, 1000); // Increase limit
    
    console.log("Speaking response:", cleanText.substring(0, 50) + "...");
    
    const speakWithVoices = () => {
      const utterance = new SpeechSynthesisUtterance(cleanText);
      utterance.lang = 'pt-PT';
      utterance.rate = 1.0;
      utterance.pitch = 1.0;
      utterance.volume = 1.0;
      
      // Try to find a Portuguese voice
      const voices = synthRef.current.getVoices();
      console.log("Available voices:", voices.length);
      
      const ptVoice = voices.find(v => v.lang.includes('pt-PT')) 
        || voices.find(v => v.lang.includes('pt-BR'))
        || voices.find(v => v.lang.includes('pt'))
        || voices[0];
      
      if (ptVoice) {
        utterance.voice = ptVoice;
        console.log("Using voice:", ptVoice.name, ptVoice.lang);
      }
      
      utterance.onstart = () => {
        console.log("Speech started");
      };
      
      utterance.onend = () => {
        console.log("Speech ended");
        setIsSpeaking(false);
        setIsProcessing(false);
        
        // Resume listening after speaking - direct start
        if (isListening) {
          setConnectionStatus("listening");
          setTimeout(() => {
            try {
              if (recognitionRef.current) {
                recognitionRef.current.start();
                console.log("Recognition restarted after speech");
              }
            } catch (e) {
              console.log("Recognition restart skipped:", e.message);
            }
          }, 800);
        } else {
          setConnectionStatus("idle");
        }
      };
      
      utterance.onerror = (e) => {
        console.error("Speech error:", e.error);
        setIsSpeaking(false);
        setIsProcessing(false);
        
        // Show error but don't block - user can still read the message
        if (e.error !== 'canceled') {
          console.log("TTS failed, but message is visible in chat");
        }
        
        if (isListening) {
          setConnectionStatus("listening");
          setTimeout(() => {
            try {
              if (recognitionRef.current) {
                recognitionRef.current.start();
                console.log("Recognition restarted after speech error");
              }
            } catch (err) {
              console.log("Recognition restart skipped:", err.message);
            }
          }, 800);
        } else {
          setConnectionStatus("idle");
        }
      };
      
      synthRef.current.speak(utterance);
    };
    
    // Wait for voices to load if needed
    const voices = synthRef.current.getVoices();
    if (voices.length === 0) {
      console.log("Waiting for voices to load...");
      synthRef.current.onvoiceschanged = () => {
        speakWithVoices();
      };
      // Fallback timeout
      setTimeout(() => {
        if (!synthRef.current.speaking) {
          console.log("Voices timeout, trying anyway...");
          speakWithVoices();
        }
      }, 500);
    } else {
      speakWithVoices();
    }
  };

  const toggleListening = () => {
    if (isListening) {
      // Stop listening
      setIsListening(false);
      setConnectionStatus("idle");
      if (recognitionRef.current) {
        recognitionRef.current.abort();
      }
      synthRef.current.cancel();
      stopVolumeAnalysis();
      setIsSpeaking(false);
      setIsProcessing(false);
      setTranscript("");
    } else {
      // Start listening
      setIsListening(true);
      setError("");
      startListening();
    }
  };

  const getStatusText = () => {
    switch (connectionStatus) {
      case "listening": return "A ouvir... fala!";
      case "hearing": return "A captar voz...";
      case "processing": return "A pensar...";
      case "speaking": return "A falar...";
      case "waiting": return "À espera...";
      case "error": return "Erro - tenta novamente";
      default: return "Clica para começar";
    }
  };

  const getStatusColor = () => {
    switch (connectionStatus) {
      case "listening": return "#22c55e";
      case "hearing": return "#eab308";
      case "processing": return "#3b82f6";
      case "speaking": return "#8b5cf6";
      case "error": return "#ef4444";
      default: return "#64748b";
    }
  };

  const clearMessages = () => {
    setMessages([]);
  };

  const playBackendAudio = (base64Audio) => {
    setIsSpeaking(true);
    setConnectionStatus("speaking");
    
    try {
      // Create audio from base64
      const audioBlob = new Blob(
        [Uint8Array.from(atob(base64Audio), c => c.charCodeAt(0))],
        { type: 'audio/mp3' }
      );
      const audioUrl = URL.createObjectURL(audioBlob);
      const audio = new Audio(audioUrl);
      
      audio.onended = () => {
        console.log("Backend audio finished");
        URL.revokeObjectURL(audioUrl);
        setIsSpeaking(false);
        setIsProcessing(false);
        
        // Resume listening after speaking - use longer delay to ensure state updates
        if (isListening) {
          setConnectionStatus("listening");
          // Increase delay and force restart
          setTimeout(() => {
            try {
              if (recognitionRef.current) {
                recognitionRef.current.start();
                console.log("Recognition restarted after audio");
              }
            } catch (e) {
              console.log("Recognition restart skipped:", e.message);
            }
          }, 800);
        } else {
          setConnectionStatus("idle");
        }
      };
      
      audio.onerror = (e) => {
        console.error("Audio playback error:", e);
        URL.revokeObjectURL(audioUrl);
        setIsSpeaking(false);
        setIsProcessing(false);
        
        // Fall back to browser TTS
        console.log("Falling back to browser TTS");
        
        if (isListening) {
          setConnectionStatus("listening");
          setTimeout(() => {
            try {
              if (recognitionRef.current) {
                recognitionRef.current.start();
                console.log("Recognition restarted after audio error");
              }
            } catch (e) {
              console.log("Recognition restart skipped:", e.message);
            }
          }, 800);
        } else {
          setConnectionStatus("idle");
        }
      };
      
      audio.play().catch(e => {
        console.error("Audio play failed:", e);
        setIsSpeaking(false);
        setIsProcessing(false);
        if (isListening) {
          setConnectionStatus("listening");
          setTimeout(() => {
            try {
              if (recognitionRef.current) {
                recognitionRef.current.start();
                console.log("Recognition restarted after play error");
              }
            } catch (err) {
              console.log("Recognition restart skipped:", err.message);
            }
          }, 800);
        } else {
          setConnectionStatus("idle");
        }
      });
      
    } catch (e) {
      console.error("Error creating audio:", e);
      setIsSpeaking(false);
      setIsProcessing(false);
      if (isListening) {
        setConnectionStatus("listening");
        setTimeout(() => {
          try {
            if (recognitionRef.current) {
              recognitionRef.current.start();
              console.log("Recognition restarted after audio create error");
            }
          } catch (err) {
            console.log("Recognition restart skipped:", err.message);
          }
        }, 800);
      } else {
        setConnectionStatus("idle");
      }
    }
  };

  const handleTextSubmit = (e) => {
    e.preventDefault();
    if (textInput.trim() && !isProcessing) {
      handleUserMessage(textInput.trim());
      setTextInput("");
    }
  };

  const toggleTextInput = () => {
    setShowTextInput(!showTextInput);
  };

  return (
    <div data-testid="voice-chat-container" className="voice-chat-container">
      {error && (
        <div className="voice-error" data-testid="voice-error">
          {error}
          <button onClick={() => setError("")} className="error-dismiss">×</button>
        </div>
      )}
      
      <div className="voice-header">
        <div className="voice-header-title">
          <span className="voice-logo">💫</span>
          <span>Dalpha Prime</span>
          <span className="voice-mode">Voz Contínua</span>
        </div>
        {messages.length > 0 && (
          <button onClick={clearMessages} className="voice-clear-btn" data-testid="voice-clear-btn">
            Limpar
          </button>
        )}
      </div>
      
      <div className="voice-messages" data-testid="voice-messages">
        {messages.length === 0 && (
          <div className="voice-welcome">
            <div className="voice-welcome-icon">🎙️</div>
            <h3>Conversa Contínua</h3>
            <p>Clica no botão e começa a falar naturalmente.</p>
            <p className="voice-tip">A conversa flui automaticamente - quando eu terminar de falar, podes continuar sem clicar!</p>
            <div className="voice-features">
              <span>📧 Enviar emails</span>
              <span>💰 Finanças</span>
              <span>🔍 Pesquisas</span>
              <span>💬 Conversar</span>
            </div>
          </div>
        )}
        {messages.map((msg, idx) => (
          <div 
            key={idx} 
            className={`voice-message ${msg.role}`}
            data-testid={`voice-message-${idx}`}
          >
            {msg.role === "assistant" && <span className="voice-avatar">💫</span>}
            <div className="voice-bubble">
              {msg.content}
              <span className="voice-time">
                {msg.timestamp?.toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' })}
              </span>
            </div>
            {msg.role === "user" && <span className="voice-avatar user">🎤</span>}
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>
      
      {transcript && (
        <div className="voice-transcript" data-testid="voice-transcript">
          <span className="transcript-wave">〰️</span>
          <span className="transcript-text">{transcript}</span>
          {/* Botão de enviar manual para mobile */}
          <button 
            onClick={() => {
              if (transcript.trim().length > 2 && !isProcessing) {
                handleUserMessage(transcript.trim());
                setTranscript("");
              }
            }}
            className="transcript-send-btn"
            data-testid="transcript-send-btn"
            disabled={isProcessing || transcript.trim().length <= 2}
          >
            ✓ Enviar
          </button>
        </div>
      )}
      
      <div className="voice-controls">
        <button
          onClick={toggleListening}
          className={`voice-btn ${isListening ? 'active' : ''}`}
          style={{ 
            '--status-color': getStatusColor(),
            '--volume': volume 
          }}
          data-testid="voice-toggle-btn"
        >
          <div className="voice-btn-inner">
            {isListening ? (
              <div className="voice-pulse-container">
                <div className="voice-pulse-ring" style={{ transform: `scale(${1 + volume * 0.5})` }}></div>
                <div className="voice-pulse-ring delay" style={{ transform: `scale(${1 + volume * 0.3})` }}></div>
                <span className="voice-icon">
                  {connectionStatus === "speaking" ? "🔊" : 
                   connectionStatus === "processing" ? "💭" : "🎤"}
                </span>
              </div>
            ) : (
              <span className="voice-mic">🎤</span>
            )}
          </div>
        </button>
        
        <div className="voice-status-container">
          <span 
            className="voice-status" 
            style={{ color: getStatusColor() }}
          >
            {getStatusText()}
          </span>
          {isListening && (
            <div className="voice-indicator">
              <span className="indicator-dot" style={{ backgroundColor: getStatusColor() }}></span>
              <span className="indicator-dot" style={{ backgroundColor: getStatusColor(), animationDelay: '0.2s' }}></span>
              <span className="indicator-dot" style={{ backgroundColor: getStatusColor(), animationDelay: '0.4s' }}></span>
            </div>
          )}
        </div>
        
        {isListening && (
          <button 
            onClick={toggleListening}
            className="voice-stop-btn"
            data-testid="voice-stop-btn"
          >
            ⏹️ Parar
          </button>
        )}
        
        <button 
          onClick={toggleTextInput}
          className="voice-text-toggle"
          data-testid="voice-text-toggle"
          title="Escrever em vez de falar"
        >
          ⌨️
        </button>
      </div>
      
      {/* Text input fallback */}
      {showTextInput && (
        <form onSubmit={handleTextSubmit} className="voice-text-form">
          <input
            type="text"
            value={textInput}
            onChange={(e) => setTextInput(e.target.value)}
            placeholder="Escreve aqui se o microfone não funcionar..."
            className="voice-text-input"
            disabled={isProcessing}
            data-testid="voice-text-input"
          />
          <button 
            type="submit" 
            className="voice-text-submit"
            disabled={isProcessing || !textInput.trim()}
            data-testid="voice-text-submit"
          >
            Enviar
          </button>
        </form>
      )}
    </div>
  );
};

export default VoiceChat;
